import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, Button, ClickAwayListener } from '@mui/material';
import { FocusEvent, useCallback, useRef, useState } from 'react';
import InterruptionActionButton from '../../../List/layout/InterruptionActionButton';
import { InterruptionDirection, InterruptionEmergencyCardView } from '../../../types';

type InterruptionsEmergencyCardActionsProps = {
  expired: boolean;
  id: string;
  item: InterruptionEmergencyCardView;
  onAccept: (id: string) => Promise<boolean>;
  onCancel: (id: string) => Promise<boolean>;
  onDismiss: (id: string) => Promise<boolean>;
  onPostpone: (id: string) => Promise<boolean>;
};

enum EmergencyCardAction {
  Accept = 'ACCEPT',
  Cancel = 'CANCEL',
  Postpone = 'POSTPONE',
  Reject = 'REJECT',
}

const ACTIONS_SX = {
  alignItems: 'center',
  display: 'flex',
  gap: 1,
  justifyContent: 'flex-end',
  mt: 1,
} as const;

const MENU_SX = {
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
  bottom: 'calc(100% + 4px)',
  boxShadow: 4,
  p: 0.5,
  position: 'absolute',
  right: 0,
  width: 192,
  zIndex: 2,
} as const;

const MENU_ITEM_SX = {
  alignItems: 'center',
  color: 'text.primary',
  display: 'flex',
  fontSize: 14,
  gap: 1,
  justifyContent: 'flex-start',
  px: 1.25,
  py: 1,
  textTransform: 'none',
  width: '100%',
  '&:hover': {
    bgcolor: 'action.hover',
  },
} as const;

const InterruptionsEmergencyCardActions = ({
  expired,
  id,
  item,
  onAccept,
  onCancel,
  onDismiss,
  onPostpone,
}: InterruptionsEmergencyCardActionsProps) => {
  const [loadingAction, setLoadingAction] = useState<EmergencyCardAction | null>(null);
  const [isRejectMenuOpen, setIsRejectMenuOpen] = useState(false);
  const rejectMenuContainerRef = useRef<HTMLDivElement | null>(null);
  const isPending = loadingAction !== null;

  const runAction = useCallback(
    async (action: EmergencyCardAction, handler: (id: string) => Promise<boolean>) => {
      if (isPending) {
        return;
      }

      setLoadingAction(action);
      try {
        await handler(id);
      } finally {
        setLoadingAction(null);
      }
    },
    [id, isPending],
  );

  const runRejectAction = useCallback(
    (action: EmergencyCardAction.Postpone | EmergencyCardAction.Reject) => {
      setIsRejectMenuOpen(false);

      if (action === EmergencyCardAction.Postpone) {
        void runAction(EmergencyCardAction.Postpone, onPostpone);
        return;
      }

      void runAction(EmergencyCardAction.Reject, onDismiss);
    },
    [onDismiss, onPostpone, runAction],
  );

  const closeRejectMenuOnBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!rejectMenuContainerRef.current?.contains(event.relatedTarget)) {
      setIsRejectMenuOpen(false);
    }
  };

  return (
    <Box sx={ACTIONS_SX}>
      {item.direction === InterruptionDirection.Incoming ? (
        <>
          <ClickAwayListener onClickAway={() => setIsRejectMenuOpen(false)}>
            <Box
              ref={rejectMenuContainerRef}
              data-emergency-action
              sx={{ position: 'relative' }}
              onBlur={closeRejectMenuOnBlur}
              onKeyDown={event => {
                if (event.key === 'Escape') {
                  setIsRejectMenuOpen(false);
                }
              }}
            >
              <InterruptionActionButton
                variant="outlined"
                color="secondary"
                size="small"
                disabled={isPending}
                minWidth={80}
                aria-haspopup="menu"
                aria-expanded={isRejectMenuOpen}
                onClick={() => setIsRejectMenuOpen(value => !value)}
                endIcon={
                  <ArrowDropDownIcon
                    sx={{
                      fontSize: 18,
                      transform: isRejectMenuOpen ? 'rotate(180deg)' : undefined,
                      transition: 'transform 0.15s ease',
                    }}
                  />
                }
              >
                {loadingAction === EmergencyCardAction.Postpone || loadingAction === EmergencyCardAction.Reject
                  ? 'Saving...'
                  : 'Reject'}
              </InterruptionActionButton>

              {isRejectMenuOpen ? (
                <Box role="menu" sx={MENU_SX}>
                  <Button
                    role="menuitem"
                    variant="text"
                    color="inherit"
                    disabled={isPending || expired}
                    startIcon={<ScheduleIcon sx={{ color: 'text.secondary', fontSize: 16 }} />}
                    sx={MENU_ITEM_SX}
                    onClick={() => runRejectAction(EmergencyCardAction.Postpone)}
                  >
                    Try later today
                  </Button>
                  <Button
                    role="menuitem"
                    variant="text"
                    color="inherit"
                    disabled={isPending || expired}
                    startIcon={<BlockIcon sx={{ color: 'text.secondary', fontSize: 16 }} />}
                    sx={MENU_ITEM_SX}
                    onClick={() => runRejectAction(EmergencyCardAction.Reject)}
                  >
                    Tomorrow
                  </Button>
                </Box>
              ) : null}
            </Box>
          </ClickAwayListener>

          <InterruptionActionButton
            data-emergency-action
            variant="outlined"
            color="success"
            size="small"
            disabled={isPending || expired}
            minWidth={80}
            onClick={() => {
              void runAction(EmergencyCardAction.Accept, onAccept);
            }}
          >
            {loadingAction === EmergencyCardAction.Accept ? 'Opening...' : 'Callback'}
          </InterruptionActionButton>
        </>
      ) : null}

      {item.direction === InterruptionDirection.Outgoing ? (
        <InterruptionActionButton
          data-emergency-action
          variant="outlined"
          color="primary"
          size="small"
          disabled={isPending}
          minWidth={80}
          onClick={() => {
            void runAction(EmergencyCardAction.Cancel, onCancel);
          }}
        >
          {loadingAction === EmergencyCardAction.Cancel ? 'Cancelling...' : 'Cancel'}
        </InterruptionActionButton>
      ) : null}
    </Box>
  );
};

export default InterruptionsEmergencyCardActions;
